import Popover from "@mui/material/Popover";
import {useMemo, useState} from "react";
import {matchesPickerItem, type PickerItem} from "./pickerItem";
import VariablePickerPanel from "./VariablePickerPanel";

export interface VariablePickerPopoverProps<I extends PickerItem = PickerItem> {
    anchorEl: HTMLElement | null;
    open: boolean;
    /** Unfiltered; the popover owns the query and filters with `matchesPickerItem`. */
    items: readonly I[];
    onSelect: (item: I) => void;
    onClose: () => void;
    emptyLabel?: string;
    width?: number;
}

/*
    The Popover's own Paper is only a positioning box: transparent, no shadow, nothing clipped, so
    the panel's outlined, shadowed Paper is the one surface the author sees and the info card can
    hang out past its edge.
 */
const POPOVER_PAPER_SX = {
    mt: "8px",
    bgcolor: "transparent",
    backgroundImage: "none",
    boxShadow: "none",
    overflow: "visible",
};

/**
 * The picker for a toolbar button: there is no "/" in a document to hold the query, so the
 * popover holds it and the panel's header becomes a real input.
 */
export function VariablePickerPopover<I extends PickerItem>({
    anchorEl,
    open,
    items,
    onSelect,
    onClose,
    emptyLabel,
    width,
}: VariablePickerPopoverProps<I>) {
    const [query, setQuery] = useState("");

    /*
        Every opening starts from an empty search, not from what was typed the last time. Cleared
        as it opens rather than as it closes, so the exit transition does not flash the full list.
     */
    const [wasOpen, setWasOpen] = useState(open);
    if (wasOpen !== open) {
        setWasOpen(open);
        if (open) setQuery("");
    }

    const filtered = useMemo(() => items.filter(item => matchesPickerItem(item, query)), [items, query]);

    return (
        <Popover
            open={open && anchorEl !== null}
            anchorEl={anchorEl}
            onClose={() => onClose()}
            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            transformOrigin={{vertical: "top", horizontal: "left"}}
            slotProps={{paper: {sx: POPOVER_PAPER_SX}}}
        >
            <VariablePickerPanel
                items={filtered}
                query={query}
                onQueryChange={setQuery}
                onSelect={onSelect}
                onClose={onClose}
                emptyLabel={emptyLabel}
                width={width}
            />
        </Popover>
    );
}

export default VariablePickerPopover;
